import type {HotelCategoryType} from '@travel-frontend/ui-components';
import type {LinkProps} from 'react-router';

import {css} from '@emotion/react';
import styled from '@emotion/styled';
import {useBreakpointUp} from '@travel-frontend/core';
import {
  LabelXSmallBold,
  Stack,
  SustainabilityHotelBadge,
  SPACING_XSMALL,
} from '@travel-frontend/ui-components';

import {HotelCategory} from '@/components/HotelCategory';
import {Name} from '@/components/HotelSummary2/components/Name';
import {testId} from '@/utils/testId';

type Props = {
  name: string;
  category: number;
  categoryType: HotelCategoryType;
  country?: string;
  city?: string;
  region?: string;
  sustainableCertifier?: string;
  onRegionClick?: () => void;
  linkProps?: Pick<LinkProps, 'to' | 'state' | 'target' | 'onClick'> & {
    as?: React.ElementType;
  };
};

const LinkedHotelName = styled.a`
  color: inherit;
  text-decoration: none;
`;

export const HotelSummary2 = ({
  category,
  categoryType,
  name,
  sustainableCertifier,
  city,
  country,
  region,
  onRegionClick,
  linkProps,
}: Props) => {
  const locationContent = [city, region, country].filter(Boolean).join(', ');
  const lgUp = useBreakpointUp('lg');

  const hotelName = linkProps ? (
    <LinkedHotelName {...linkProps}>{name}</LinkedHotelName>
  ) : (
    <span>{name}</span>
  );

  return (
    <Stack spacing={SPACING_XSMALL}>
      <LabelXSmallBold
        data-testid={testId(`hotel-region`)}
        onClick={onRegionClick}
        as={onRegionClick ? 'button' : undefined}
        css={(theme) =>
          onRegionClick &&
          css`
            cursor: pointer;
            background-color: transparent;
            border: none;
            padding: 0;
            margin: 0;
            color: ${theme.colors.interactionSecondary};
            text-align: left;
          `
        }
      >
        {locationContent}
      </LabelXSmallBold>
      <Name data-testid="find-hotellist-hotel-name">
        {hotelName}
        {sustainableCertifier && (
          <SustainabilityHotelBadge
            size={lgUp ? 'large' : 'medium'}
            certifyingAuthority={sustainableCertifier}
          />
        )}
        <HotelCategory
          value={category}
          type={categoryType}
          data-testid="find-hotellist-hotel-category"
        />
      </Name>
    </Stack>
  );
};
